/**
 * Édition d'un cours : métadonnées, plan, cycle de vie (docs/11 §2 et §6).
 *
 * Trois choses sont **décidées par le serveur** et seulement affichées ici :
 * `can_edit`, `can_delete` et `publication_blockers`. L'écran ne recalcule
 * aucune de ces règles — il montrerait sinon un bouton « supprimer » sur un
 * cours publié (409 garanti), ou une liste de conditions qui divergerait de
 * celle que la publication applique réellement.
 */
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import {
  useCourse,
  useCourseDomains,
  useCourseLifecycle,
  useCreateLesson,
  useDeleteCourse,
  useDeleteLesson,
  useReorderLessons,
  useUpdateCourse,
} from "@/features/catalog/api/useCatalog";
import { CoverEditor } from "@/features/catalog/components/CoverEditor";
import { StatusBadge } from "@/features/catalog/components/StatusBadge";
import { asDocument, toStored, type ContentDocument } from "@/shared/richtext/document";
import { RichTextEditor } from "@/shared/richtext/RichTextEditor";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

const LEVELS = ["beginner", "intermediate", "advanced"] as const;
const VISIBILITIES = ["public", "unlisted", "private"] as const;

const FIELD_CLASSES =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm " +
  "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

export function CourseEditorPage() {
  const { t } = useTranslation("catalog");
  const navigate = useNavigate();
  const { courseId } = useParams({ from: "/teach/teach/courses/$courseId" });

  const course = useCourse(courseId);
  const domains = useCourseDomains();
  const updateCourse = useUpdateCourse(courseId);
  const lifecycle = useCourseLifecycle(courseId);
  const deleteCourse = useDeleteCourse();
  const createLesson = useCreateLesson(courseId);
  const deleteLesson = useDeleteLesson(courseId);
  const reorder = useReorderLessons(courseId);
  const errorMessage = useApiErrorMessage();

  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    domain_id: "",
    level: "",
    visibility: "public",
  });
  // Rangée à part du reste du formulaire : `RichTextEditor` n'est pas contrôlé,
  // et la faire vivre dans le même objet ferait recomposer l'éditeur à chaque
  // frappe dans le titre.
  // `undefined` tant que la fiche n'est pas arrivée : `RichTextEditor` lit son
  // contenu à la construction, et le monter trop tôt afficherait une
  // description vide sur un cours qui en a une.
  const [description, setDescription] = useState<ContentDocument | null | undefined>(undefined);
  const [newLessonTitle, setNewLessonTitle] = useState("");

  // Le formulaire est initialisé une fois la fiche chargée, puis vit sa vie :
  // le réinitialiser à chaque `course.data` écraserait ce que l'enseignant est
  // en train de taper dès qu'une invalidation de cache passe par là.
  useEffect(() => {
    if (course.data === undefined) {
      return;
    }
    setForm({
      title: course.data.course.title,
      subtitle: course.data.course.subtitle ?? "",
      domain_id: course.data.course.domain_id,
      level: course.data.course.level ?? "",
      visibility: course.data.course.visibility,
    });
    setDescription(asDocument(course.data.description));
  }, [course.data?.course.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (course.isPending) {
    return <Skeleton className="mx-auto my-8 h-96 w-full max-w-4xl" label={t("editor.metadata")} />;
  }
  if (course.isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <ErrorState error={course.error} />
      </div>
    );
  }

  const detail = course.data;
  const lessons = detail.lessons;

  const saveMetadata = () => {
    updateCourse.mutate({
      title: form.title,
      // Une chaîne vidée est envoyée à `null` : c'est ainsi que le serveur
      // distingue « effacer » de « ne pas toucher » (docs/08 §3).
      subtitle: form.subtitle === "" ? null : form.subtitle,
      description: toStored(description),
      domain_id: form.domain_id,
      level: form.level === "" ? null : (form.level as (typeof LEVELS)[number]),
      visibility: form.visibility as (typeof VISIBILITIES)[number],
    });
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= lessons.length) {
      return;
    }
    const ordered = lessons.map((lesson) => lesson.id);
    const [moved] = ordered.splice(index, 1);
    if (moved !== undefined) {
      ordered.splice(target, 0, moved);
    }
    // La séquence **complète** part au serveur : il refuse une liste partielle,
    // qui le laisserait inventer la place des leçons absentes (docs/11 §6).
    reorder.mutate(ordered);
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">{detail.course.title}</h1>
          <StatusBadge status={detail.course.status} />
        </div>
        <div className="flex flex-wrap gap-2">
          {detail.course.status !== "published" && (
            <Button
              disabled={lifecycle.isPending || detail.publication_blockers.length > 0}
              onClick={() => {
                lifecycle.mutate("publish");
              }}
            >
              {t("editor.publish")}
            </Button>
          )}
          {detail.course.status === "published" && (
            <Button
              variant="secondary"
              disabled={lifecycle.isPending}
              onClick={() => {
                lifecycle.mutate("unpublish");
              }}
            >
              {t("editor.unpublish")}
            </Button>
          )}
          {detail.course.status !== "archived" && (
            <Button
              variant="secondary"
              disabled={lifecycle.isPending}
              onClick={() => {
                lifecycle.mutate("archive");
              }}
            >
              {t("editor.archive")}
            </Button>
          )}
          {/* `can_delete` vient du serveur et vaut faux dès la publication :
              l'invariant INV-C6 n'est pas une question de permission. */}
          {detail.can_delete && (
            <Button
              variant="danger"
              disabled={deleteCourse.isPending}
              onClick={() => {
                deleteCourse.mutate(courseId, {
                  onSuccess: () => {
                    void navigate({ to: "/teach/courses" });
                  },
                });
              }}
            >
              {t("editor.delete")}
            </Button>
          )}
        </div>
      </header>

      {lifecycle.isError && <Alert tone="error">{errorMessage(lifecycle.error)}</Alert>}

      {detail.publication_blockers.length > 0 && (
        <Alert tone="warning">
          <p className="font-medium">{t("editor.blockersTitle")}</p>
          <ul className="mt-1 list-disc pl-5 text-sm">
            {detail.publication_blockers.map((reason) => (
              <li key={reason}>{t(`blockers.${reason}`)}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Card title={t("editor.metadata")}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium" htmlFor="course-title">
              {t("mine.courseTitle")}
            </label>
            <input
              id="course-title"
              className={FIELD_CLASSES}
              value={form.title}
              onChange={(event) => {
                setForm({ ...form, title: event.target.value });
              }}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium" htmlFor="course-subtitle">
              {t("editor.subtitle")}
            </label>
            <input
              id="course-subtitle"
              className={FIELD_CLASSES}
              value={form.subtitle}
              onChange={(event) => {
                setForm({ ...form, subtitle: event.target.value });
              }}
            />
          </div>
          <div className="sm:col-span-2">
            {description !== undefined && (
              <RichTextEditor
                label={t("editor.description")}
                documentKey={detail.course.id}
                content={description}
                onChange={setDescription}
                hint={t("editor.descriptionHint")}
              />
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="course-domain">
              {t("catalog.domain")}
            </label>
            <select
              id="course-domain"
              className={FIELD_CLASSES}
              value={form.domain_id}
              onChange={(event) => {
                setForm({ ...form, domain_id: event.target.value });
              }}
            >
              {domains.data?.map((domain) => (
                <option key={domain.id} value={domain.id}>
                  {domain.name_i18n["fr"] ?? domain.slug}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="course-level">
              {t("catalog.level")}
            </label>
            <select
              id="course-level"
              className={FIELD_CLASSES}
              value={form.level}
              onChange={(event) => {
                setForm({ ...form, level: event.target.value });
              }}
            >
              <option value="">{t("editor.noLevel")}</option>
              {LEVELS.map((level) => (
                <option key={level} value={level}>
                  {t(`levels.${level}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="course-visibility">
              {t("editor.visibility")}
            </label>
            <select
              id="course-visibility"
              className={FIELD_CLASSES}
              value={form.visibility}
              onChange={(event) => {
                setForm({ ...form, visibility: event.target.value });
              }}
            >
              {VISIBILITIES.map((visibility) => (
                <option key={visibility} value={visibility}>
                  {t(`visibility.${visibility}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button disabled={updateCourse.isPending} onClick={saveMetadata}>
            {t("editor.save")}
          </Button>
          {updateCourse.isSuccess && (
            <span className="text-sm text-green-700 dark:text-green-400">{t("editor.saved")}</span>
          )}
        </div>
        {updateCourse.isError && (
          <Alert tone="error" className="mt-3">
            {errorMessage(updateCourse.error)}
          </Alert>
        )}
      </Card>

      <CoverEditor courseId={courseId} coverFileId={detail.cover_file_id} />

      <Card title={t("editor.curriculum")}>
        {lessons.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("editor.noLesson")}</p>
        ) : (
          <ol className="space-y-2">
            {lessons.map((lesson, index) => (
              <li
                key={lesson.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800"
              >
                <span className="text-slate-500 dark:text-slate-400">{lesson.position}.</span>
                <span className="text-sm font-medium">{lesson.title}</span>
                <StatusBadge status={lesson.status} />
                <div className="ml-auto flex gap-1">
                  <Button
                    variant="ghost"
                    aria-label={t("editor.moveUp")}
                    disabled={index === 0 || reorder.isPending}
                    onClick={() => {
                      move(index, -1);
                    }}
                  >
                    {t("editor.upGlyph")}
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={t("editor.moveDown")}
                    disabled={index === lessons.length - 1 || reorder.isPending}
                    onClick={() => {
                      move(index, 1);
                    }}
                  >
                    {t("editor.downGlyph")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      void navigate({
                        to: "/teach/courses/$courseId/lessons/$lessonId",
                        params: { courseId, lessonId: lesson.id },
                      });
                    }}
                  >
                    {t("editor.editLesson")}
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={t("editor.removeLesson")}
                    disabled={deleteLesson.isPending}
                    onClick={() => {
                      deleteLesson.mutate(lesson.id);
                    }}
                  >
                    {t("editor.removeGlyph")}
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        )}

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="grow">
            <label className="mb-1 block text-sm font-medium" htmlFor="new-lesson-title">
              {t("editor.newLesson")}
            </label>
            <input
              id="new-lesson-title"
              className={FIELD_CLASSES}
              value={newLessonTitle}
              onChange={(event) => {
                setNewLessonTitle(event.target.value);
              }}
            />
          </div>
          <Button
            disabled={newLessonTitle.trim() === "" || createLesson.isPending}
            onClick={() => {
              createLesson.mutate(
                { title: newLessonTitle },
                {
                  onSuccess: () => {
                    setNewLessonTitle("");
                  },
                },
              );
            }}
          >
            {t("editor.addLesson")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
